import { Service, type ServiceFromServer } from "@/code/service";
import { reactive, ref, type Ref } from "vue";
import { skapi } from "@/main";
import { ENDPOINTS } from "@/code/endpoints";
import Pager from "@/code/pager";
import { watch } from "vue";
import { planSpecsLoaded, formatSpecs } from "@/code/service-spec";

export let serviceIdList: Ref<string[]> = ref([]);
export let serviceList: { [key: string]: Service | Promise<Service> } = reactive({});
export let endOfList = ref(false);
export let didTheFirstPull = false;
export let currentService: Ref<Service> = ref(null);
export let serviceUsers: { [service_id: string]: any } = {};
export let serviceRecords: { [service_id: string]: any } = {};
export let serviceAutoMails: { [service_id: string]: any } = {};
export let serviceBulkMails: { [service_id: string]: any } = {};
export let serviceLoggers: { [service_id: string]: any } = {};
export let serviceClientSecrets: { [service_id: string]: any } = {};
export let serviceTickets: { [service_id: string]: any } = {};

export const projectNameExamples = [
    "Customer portal",
    "Online store",
    "Community forum",
    "Booking system",
    "Team wiki",
    "Newsletter site",
    "Photo sharing app",
    "Course platform",
];

export const projectDescriptionExamples = [
    "Customer portal with login, orders and file uploads.",
    "Online store with product listings, carts and order history.",
    "Community forum with member profiles, posts and comments.",
    "Booking system with user accounts, reservations and email reminders.",
    "Internal wiki with restricted access and document uploads.",
    "Newsletter site with subscriber signup and scheduled mailings.",
    "Photo sharing app with albums, uploads and public galleries.",
    "Course platform with student logins, lessons and progress tracking.",
];

export const pickProjectNamePlaceholder = () =>
    projectNameExamples[Math.floor(Math.random() * projectNameExamples.length)];

// Name and description are drawn from the SAME index so the two placeholders
// describe one project rather than two unrelated ones.
export const pickProjectPlaceholders = () => {
    const idx = Math.floor(Math.random() * projectNameExamples.length);
    return {
        name: projectNameExamples[idx],
        description: projectDescriptionExamples[idx] || "",
    };
};

// ─── Project-entry prefetch: records / users ─────────────────────────────────
// Fired (fire-and-forget) from the service shell's run() on project entry so
// the Database / Users tabs paint from a warm module cache instead of
// blocking on their first fetch. Each builds the SAME pager + first-page fetch
// the page's own cold path builds (params must stay in sync with
// records.vue/users.vue setUpNewPageList + getPage), and PUBLISHES it only if
// the slot is still empty, if the page mounted mid-prefetch and built its
// own pager, the prefetched one is discarded, never clobbering a live pager.
const recordsPrefetchInflight: { [service_id: string]: Promise<void> } = {};
export function prefetchServiceRecords(service: Service): Promise<void> {
    try {
        const sid = service?.id;
        if (!sid || serviceRecords[sid]) return Promise.resolve();
        if (recordsPrefetchInflight[sid]) return recordsPrefetchInflight[sid];
        const p = (async () => {
            const pager = await Pager.init({
                id: "record_id",
                resultsPerPage: 12,
                sortBy: "record_id",
                order: "desc",
            });
            const fetched: any = await skapi.getRecords(
                { service: sid },
                { fetchMore: false, limit: 120 },
            );
            // Pager cannot hold functions (same strip as records.vue getPage).
            (fetched?.list || []).forEach((r: any) => {
                if (r.bin) {
                    for (let k in r.bin) {
                        r.bin[k] = r.bin[k].map((f: any) => {
                            delete f.getFile;
                            return f;
                        });
                    }
                }
            });
            (pager as any).endOfList = fetched.endOfList;
            if (fetched.list?.length) {
                await pager.insertItems(fetched.list);
            }
            if (!serviceRecords[sid]) {
                serviceRecords[sid] = pager;
            }
        })().catch(() => { }).finally(() => { delete recordsPrefetchInflight[sid]; });
        recordsPrefetchInflight[sid] = p;
        return p;
    } catch {
        return Promise.resolve();
    }
}

const usersPrefetchInflight: { [service_id: string]: Promise<void> } = {};
export function prefetchServiceUsers(service: Service): Promise<void> {
    try {
        const sid = service?.id;
        if (!sid || serviceUsers[sid]) return Promise.resolve();
        if (usersPrefetchInflight[sid]) return usersPrefetchInflight[sid];
        const p = (async () => {
            const pager = await Pager.init({
                id: "user_id",
                resultsPerPage: 12,
                sortBy: "timestamp",
                order: "desc",
            });
            const fetched: any = await skapi.getUsers(
                {
                    service: sid,
                    searchFor: "timestamp",
                    value: Date.now(),
                    condition: "<=",
                },
                { fetchMore: false, ascending: false, limit: 120 },
            );
            (pager as any).endOfList = fetched.endOfList;
            if (fetched.list?.length) {
                await pager.insertItems(fetched.list);
            }
            if (!serviceUsers[sid]) {
                serviceUsers[sid] = pager;
            }
        })().catch(() => { }).finally(() => { delete usersPrefetchInflight[sid]; });
        usersPrefetchInflight[sid] = p;
        return p;
    } catch {
        return Promise.resolve();
    }
}

// How long after a service lands in the list before its Stripe subscription
// fetch fires. Long enough that a prompt "click into a project" gets its entry
// fetches into the shared request queue first; short enough that the
// cancel/past-due badges still feel immediate on the list.
const STRIPE_SUBS_DEFER_MS = 2500;

/**
 * formatSpecs clones modelSpecs when a Service is constructed, so a project
 * loaded before GET /plan answered keeps the blank built-in limits: no meters,
 * no hard stop flag, and plan-copy's limit checks read "unknown" forever. Once
 * the served limits land, rebuild every loaded project's snapshot and its
 * derived dates through the reactive instance.
 */
watch(planSpecsLoaded, (loaded) => {
    if (!loaded) return;
    for (const id in serviceList) {
        const s: any = serviceList[id];
        if (s instanceof Promise || !s?.service) continue;
        s.formattedSpecs = formatSpecs(s.service);
        s.applySubscriptionDerived?.();
    }
});

export let addServiceToList = (serviceListToAdd: ServiceFromServer[]) => {
    serviceListToAdd.forEach((srvcObj: ServiceFromServer) => {
        let serviceId = srvcObj.service;
        if (!serviceList[serviceId]) {
            serviceList[serviceId] = Service.load(srvcObj).then(s => {
                if (s) {
                    serviceList[serviceId] = s;
                    // Stripe resolves in the BACKGROUND and is now DEFERRED a few
                    // seconds past load: the ctor used to fire one api.stripe.com
                    // request per listed project immediately, and the SDK's shared
                    // request queue drains in whole cycles, so clicking into a
                    // project right then queued its entry fetches behind the
                    // slowest Stripe call. The delay gives entry navigations a
                    // clean queue window; the cancel/past-due badges just settle
                    // a moment later. Derived fields are re-applied THROUGH the
                    // reactive instance (serviceList[serviceId] is the same proxy
                    // Vue uses for currentService), the raw writes the fetch
                    // makes don't trigger Vue reactivity on their own.
                    setTimeout(() => {
                        const reactiveInst: any = serviceList[serviceId];
                        if (!reactiveInst || typeof reactiveInst.ensureStripeSubscription !== 'function') return;
                        reactiveInst.ensureStripeSubscription().then(() => {
                            if (typeof reactiveInst.applySubscriptionDerived === 'function') {
                                reactiveInst.applySubscriptionDerived();
                            }
                        }).catch(() => { });
                    }, STRIPE_SUBS_DEFER_MS);
                }

                return s;
            });

            if (serviceIdList.value.indexOf(serviceId) === -1) {
                serviceIdList.value.push(serviceId);
            }
        }
    });

    let promisesToWait = Object.values(serviceList).filter(s => s instanceof Promise) as Promise<Service>[];

    return Promise.all(promisesToWait).then(() => {
        // 다 불러오고 난 후에 timestamp 순으로 정렬 체크
        serviceIdList.value.sort((a, b) => {
            let timeA =
                new Date(
                    (serviceList[a] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            let timeB =
                new Date(
                    (serviceList[b] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            return timeB - timeA;
        });
    });
}

export let pullServices = async (options?: { service_id_to_fetch?: string; service_id_to_exclude?: string[] }) => {
    let {
        admin_private_endpoint
    } = await ENDPOINTS;

    let service_id_to_fetch = options?.service_id_to_fetch || null;

    // console.log("Pulling services with options:", options);
    if (service_id_to_fetch && serviceList[service_id_to_fetch]) {
        // 이미 로드된 서비스인 경우 바로 반환
        return serviceList[service_id_to_fetch];
    }

    let requestOptions: any = {
        service: skapi.service,
        owner: skapi.owner,
        // service_id_to_fetch
        // service_id_to_exclude
    };

    if (service_id_to_fetch) {
        requestOptions.service_id = service_id_to_fetch;
    }

    if (options?.service_id_to_exclude) {
        requestOptions.service_id_to_exclude = options.service_id_to_exclude;
    }

    let fetchOptions: any = {
        limit: 25,
        fetchMore: service_id_to_fetch ? false : didTheFirstPull ? !endOfList.value : false, // 첫 pull 때는 fetchMore 무조건 false
    };

    let srvcs = await skapi.util
        .request(
            admin_private_endpoint + "pull-services",
            requestOptions,
            {
                auth: true,
                fetchOptions,
            }
        )


    if (service_id_to_fetch) {
        if (!srvcs?.list.length) {
            return;
        }
    } else {
        endOfList.value = srvcs?.endOfList;
        didTheFirstPull = true;
    }

    addServiceToList(srvcs.list);

    if (service_id_to_fetch) {
        // wait for the specific service to load
        return serviceList[service_id_to_fetch];
    }
    else {
        return serviceList;
    }
};